import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { PaginatedResponse, GameSummaryDto, GameSetSummaryDto, GameProductSummaryDto } from 'types/v2/catalog';
import { GradingSearchResultDto } from 'types/v2/gradings';


// Optional REACT_APP_API_SERVER override, e.g. http://localhost:8080 for local
// development. Unset (the deployed default) means production. See the README.
const API_SERVER = process.env.REACT_APP_API_SERVER || "https://app.premiercardgrading.com";

/* GET /v2/catalog/gradedLanguages (R5). One row per published language name (R8). */
export type GradedLanguageCount = { language: string; count: number };

export type GradedLanguages = {
  gameId: number | null;
  setId: number | null;
  total: number;
  notRecorded: number;
  languages: GradedLanguageCount[];
};

/**
 * RTK Query API slice for v2 public catalog and grading endpoints.
 * Provides paginated catalog browsing and grading search with proper TypeScript types.
 *
 * Cache configuration: 30 minute staleTime matching Phase 3 decision for catalog data.
 */
export const pcgPublicV2Api = createApi({
  reducerPath: 'pcgPublicV2Api',
  baseQuery: fetchBaseQuery({ baseUrl: `${API_SERVER}/public/api` }),
  keepUnusedDataFor: 1800, // 30 minutes (matching Phase 3 staleTime decision)
  endpoints: (builder) => ({
    /**
     * Get every game the pop report lists (unpaginated - small dataset)
     * GET /public/api/v2/catalog/games?scope=population
     *
     * scope=population adds unreviewed and archived games that have gradings, flagged
     * `reviewed` / `archived`. A backend without it ignores the param and returns the
     * reviewed games with no flags, which the callers read as live.
     */
    getGames: builder.query<GameSummaryDto[], void>({
      query: () => '/v2/catalog/games?scope=population',
    }),

    /**
     * Get game sets filtered by gameId with server-side pagination
     * GET /public/api/v2/catalog/sets?gameId=1&scope=population&offset=0&limit=50
     *
     * scope=population adds archived sets that have gradings, flagged `archived`.
     * nameLike keeps only sets whose name contains it, before paging (R4 of
     * docs/specs/pop-report-followups.md); callers pass it through usablePartName first.
     */
    getSets: builder.query<PaginatedResponse<GameSetSummaryDto[]>, {
      gameId?: number;
      nameLike?: string;
      offset?: number;
      limit?: number;
    }>({
      query: ({ gameId, nameLike, offset = 0, limit = 50 }) => {
        const params = new URLSearchParams();
        if (gameId !== undefined) params.append('gameId', String(gameId));
        params.append('scope', 'population');
        if (nameLike) params.append('nameLike', nameLike);
        params.append('offset', String(offset));
        params.append('limit', String(limit));
        return `/v2/catalog/sets?${params.toString()}`;
      },
    }),

    /**
     * Get game products filtered by setId with server-side pagination
     * GET /public/api/v2/catalog/products?setId=5&offset=0&limit=50
     */
    getProducts: builder.query<PaginatedResponse<GameProductSummaryDto[]>, {
      setId?: number;
      offset?: number;
      limit?: number;
    }>({
      query: ({ setId, offset = 0, limit = 50 }) => {
        const params = new URLSearchParams();
        if (setId !== undefined) params.append('setId', String(setId));
        params.append('offset', String(offset));
        params.append('limit', String(limit));
        return `/v2/catalog/products?${params.toString()}`;
      },
    }),

    /**
     * Get products with grading counts for a game/set (v1 endpoint - has grade breakdown data)
     * GET /public/api/v1/gameInfo/:gameCode/set/:setName?includeCounts=true
     */
    getProductsWithCounts: builder.query<any[], { gameCode: string; setName: string }>({
      query: ({ gameCode, setName }) =>
        `/v1/gameInfo/${encodeURIComponent(gameCode)}/set/${encodeURIComponent(setName)}?includeCounts=true`,
    }),

    /**
     * Population breakdown, global or scoped to one game or one set (spec 9.1, D13).
     * GET /public/api/v2/catalog/breakdown[?gameId=1|?setId=42]
     *
     * The endpoint is D11's separate backend MR and 404s in production today, so this is
     * written against the contract alone. Three things are deliberately absent and must
     * stay absent (spec 9.2): no retry() wrapper around the base query, and no
     * refetchOnFocus / refetchOnReconnect / refetchOnMountOrArgChange here. RTK Query
     * does not cache errors, so home -> game -> home genuinely re-fires; that is one
     * attempt per route mount, not a retry loop.
     *
     * Typed `unknown` on purpose. Declaring BreakdownFigures would assert a shape nothing
     * has checked; parseBreakdown() is what decides whether the body is the contract, and
     * it takes unknown.
     */
    getCatalogBreakdown: builder.query<unknown, { gameId?: number; setId?: number }>({
      query: ({ gameId, setId }) => {
        const params = new URLSearchParams();
        if (gameId !== undefined) params.append('gameId', String(gameId));
        if (setId !== undefined) params.append('setId', String(setId));
        const search = params.toString();
        return `/v2/catalog/breakdown${search ? `?${search}` : ''}`;
      },
    }),

    /**
     * Graded-card counts by language for one game, one set or one card's product (R5,
     * R5b of docs/specs/pop-report-followups.md).
     * GET /public/api/v2/catalog/gradedLanguages?gameId=2 | ?setId=42 | ?productId=77
     *
     * Pass exactly one of the three ids; the backend 400s on none or more than one.
     * `languages` holds only allow-listed names (R8), sorted by count, and
     * `total - notRecorded` is their sum.
     */
    getGradedLanguages: builder.query<GradedLanguages, { gameId?: number; setId?: number; productId?: number }>({
      query: ({ gameId, setId, productId }) => {
        const params = new URLSearchParams();
        if (gameId !== undefined) params.append('gameId', String(gameId));
        if (setId !== undefined) params.append('setId', String(setId));
        if (productId !== undefined) params.append('productId', String(productId));
        return `/v2/catalog/gradedLanguages?${params.toString()}`;
      },
    }),

    /**
     * Search gradings with pagination
     * Note: This endpoint is for Plan 03 - grading search migration
     * GET /public/api/v2/catalog/gradings/search?gameCode=PKM&cardName=pikachu&offset=0&limit=50
     */
    searchGradings: builder.query<PaginatedResponse<GradingSearchResultDto[]>, {
      gameCode?: string;
      setName?: string;
      /* Part of a set name (R3); callers pass it through usablePartName first. */
      setNameLike?: string;
      cardNumber?: string;
      cardName?: string;
      finish?: string;
      grade?: string;
      finalGrade?: string;
      centeringGrade?: string;
      cornersGrade?: string;
      edgesGrade?: string;
      surfaceGrade?: string;
      gradedId?: string;
      useOr?: boolean;
      offset?: number;
      limit?: number;
    }>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== '') {
            searchParams.append(key, String(value));
          }
        });
        return `/v2/catalog/gradings/search?${searchParams.toString()}`;
      },
    }),
  }),
});

export const {
  useGetGamesQuery,
  useGetSetsQuery,
  useGetProductsQuery,
  useGetProductsWithCountsQuery,
  useGetCatalogBreakdownQuery,
  useGetGradedLanguagesQuery,
  useSearchGradingsQuery,
} = pcgPublicV2Api;
